/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import {
	Container,
	Box,
	Flex,
	Text,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalCloseButton,
	ModalBody,
	Divider,
	PopoverBody,
	PopoverHeader,
	PopoverCloseButton,
	PopoverArrow,
	PopoverContent,
	PopoverTrigger,
	Popover,
	ListItem,
	UnorderedList,
	Spinner,
	Select,
	SimpleGrid,
} from '@chakra-ui/react';
import moment from 'moment';
import c from '../../constant';
import './style.css';
import useStore from '../../store';
import RichTextEditorViewer from '../../components/form/RichTextEditorField/viewer';
import AdditionalAttachment from '../NoteOfPresentation/components/additional-attachment/additional-attachment';
import DownloadAndViewButton from '../../components/Files/DownloadAndViewButton';
import { AttachmentIcon } from '@chakra-ui/icons';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import htmlDiff from 'htmldiff-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const leftLabelWidth = '165px';

const QUERY_GET_TIMELINE_JSON = gql`
	query getTimelineJson($timelineId: String!) {
		getNpTimelineJSON(timelineId: $timelineId)
	}
`;

const QUERY_GET_TIMELINE = gql`
	query ($id: String!) {
		getNpById(id: $id) {
			timelines {
				id
				createdAt
				user {
					id
					username
					fullName
					fullNameKhmer
				}
				action
				description
				timelineHistory
			}
		}
	}
`;

function EditedNPModal({ id, onCloseModal, timeline = null }) {
	const { t } = useTranslation();
	const { currentLang } = useStore((state) => state.currentLang);
	moment.locale(currentLang === 'kh' ? 'km' : 'en-gb');

	const [historyData, setHistoryData] = useState(null);
	const [subject, setSubject] = useState('');
	const [references, setReferences] = useState([]);
	const [description, setDescription] = useState('');
	const [additionalAttachmentData, setAdditionalAttachmentData] = useState([]);
	const [attachings, setAttachings] = useState([]);
	const [timelineList, setTimelineList] = useState([]);
	const [fromTimelineId, setFromTimelineId] = useState(null);
	const [toTimelineId, setToTimelineId] = useState(null);
	const [fromHistory, setFromHistory] = useState(null);
	const [toHistory, setToHistory] = useState(null);
	const [subjectDiff, setSubjectDiff] = useState('');
	const [referencesDiff, setReferencesDiff] = useState('');
	const [attachmentDiff, setAttachmentDiff] = useState('');
	const [descriptionDiff, setDescriptionDiff] = useState('');
	const [otherAttachmentsDiff, setOtherAttachmentsDiff] = useState('');

	const [loadTimelineJson, { data: timelineJsonData, loading: loadTimelineJsonData }] = useLazyQuery(QUERY_GET_TIMELINE_JSON, {
		fetchPolicy: 'no-cache',
	});

	const [loadTimeline, { data }] = useLazyQuery(QUERY_GET_TIMELINE, {
		fetchPolicy: 'no-cache',
	});

	const [loadFromTimelineJson] = useLazyQuery(QUERY_GET_TIMELINE_JSON, {
		fetchPolicy: 'no-cache',
		onCompleted: (data) => {
			try {
				setFromHistory(normalizeHistory(JSON.parse(data?.getNpTimelineJSON)));
			} catch {
				setFromHistory(null);
			}
		},
	});

	const [loadToTimelineJson] = useLazyQuery(QUERY_GET_TIMELINE_JSON, {
		fetchPolicy: 'no-cache',
		onCompleted: (data) => {
			try {
				setToHistory(normalizeHistory(JSON.parse(data?.getNpTimelineJSON)));
			} catch {
				setToHistory(null);
			}
		},
	});

	const sortByNo = (items = []) =>
		[...items]?.sort((a, b) => {
			if (a?.no !== b?.no) return (a?.no || 0) - (b?.no || 0);
			return (a?.description || a?.label || '').localeCompare(b?.description || b?.label || '');
		});

	const normalizeHistory = (history) => {
		if (!history) return null;

		return {
			...history,
			references: sortByNo(history?.references || []),
			attachments: sortByNo(history?.attachments || []),
			attachings: sortByNo(history?.attachings || []),
		};
	};

	const joinListForDiff = (items = [], field = 'description') => {
		return sortByNo(items)
			.map((item) => item?.[field] || '')
			.join('<br/>');
	};

	const diffToUl = (diffHtml) => {
		if (!diffHtml) return '';

		const items = diffHtml
			.split(/<br\s*\/?>/gi)
			.map((s) => s.replace(/<(ins|del|span)[^>]*>\s*<\/\1>/gi, '').trim())
			.filter((s) => s.replace(/<[^>]+>/g, '').trim().length > 0);

		return items.length ? `<ul>${items.map((x) => `<li>${x}</li>`).join('')}</ul>` : '';
	};

	useEffect(() => {
		if (!fromHistory || !toHistory) {
			setSubjectDiff('');
			setReferencesDiff('');
			setDescriptionDiff('');
			setAttachmentDiff('');
			setOtherAttachmentsDiff('');
			return;
		}

		try {
			const beforeSubject = fromHistory?.subject || '';
			const afterSubject = toHistory?.subject || '';

			const subDiff = new htmlDiff(beforeSubject, afterSubject).build();
			setSubjectDiff(subDiff.replace(/<br\s*\/?>/g, ''));

			const beforeRef = joinListForDiff(fromHistory?.references, 'description');
			const afterRef = joinListForDiff(toHistory?.references, 'description');
			const refDiff = new htmlDiff(beforeRef, afterRef).build();
			setReferencesDiff(diffToUl(refDiff.replace(/<br\s*\/?>/g, '<br/>')));

			const beforeDesc = fromHistory?.description || '';
			const afterDesc = toHistory?.description || '';
			const descDiff = new htmlDiff(beforeDesc, afterDesc).build();
			setDescriptionDiff(descDiff.replace(/<br\s*\/?>/g, ''));

			const beforeAtt = joinListForDiff(fromHistory?.attachings, 'description');
			const afterAtt = joinListForDiff(toHistory?.attachings, 'description');
			const attDiff = new htmlDiff(beforeAtt, afterAtt).build();
			setAttachmentDiff(diffToUl(attDiff.replace(/<br\s*\/?>/g, '<br/>')));

			const beforeOtherAtt = joinListForDiff(fromHistory?.attachments, 'label');
			const afterOtherAtt = joinListForDiff(toHistory?.attachments, 'label');
			const otherAttDiff = new htmlDiff(beforeOtherAtt, afterOtherAtt).build();
			setOtherAttachmentsDiff(diffToUl(otherAttDiff.replace(/<br\s*\/?>/g, '<br/>')));
		} catch (err) {
			console.error('Failed to generate diffs', err);
			setSubjectDiff('');
			setReferencesDiff('');
			setDescriptionDiff('');
			setAttachmentDiff('');
			setOtherAttachmentsDiff('');
		}
	}, [fromHistory, toHistory]);

	useEffect(() => {
		if (data?.getNpById?.timelines) {
			const sortedTimelines = [...data?.getNpById?.timelines]
				.filter((item) => item?.timelineHistory)
				.sort((a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime());

			setTimelineList(sortedTimelines);
		}
	}, [data]);

	useEffect(() => {
		if (id) {
			loadTimeline({ variables: { id } });
		}
	}, [id]);

	useEffect(() => {
		if (!timeline?.id) {
			setHistoryData(null);
			return;
		}
		loadTimelineJson({ variables: { timelineId: timeline.id } });
	}, [timeline?.id]);

	useEffect(() => {
		const json = timelineJsonData?.getNpTimelineJSON;
		if (!json) {
			setHistoryData(null);
			return;
		}
		try {
			setHistoryData(normalizeHistory(JSON.parse(json)));
		} catch (e) {
			console.error('Failed to parse timeline json', e);
			setHistoryData(null);
		}
	}, [timelineJsonData]);

	useEffect(() => {
		if (!historyData) {
			setSubject('');
			setDescription('');
			setReferences([]);
			setAdditionalAttachmentData([]);
			setAttachings([]);
			return;
		}

		setSubject(historyData?.subject || '');
		setReferences(historyData?.references || []);
		setDescription(historyData?.description || '');
		setAdditionalAttachmentData(historyData?.attachments || []);
		setAttachings(historyData?.attachings || []);
	}, [historyData]);

	useEffect(() => {
		if (timeline?.id) {
			setToTimelineId(timeline.id);
			loadToTimelineJson({ variables: { timelineId: timeline.id } });
		} else {
			setFromTimelineId(null);
			setToTimelineId(null);
			setFromHistory(null);
			setToHistory(null);
		}
	}, [timeline?.id]);

	const timelinesWithHistory = useMemo(() => {
		return (timelineList || []).filter((t) => t?.timelineHistory !== null);
	}, [timelineList]);

	return (
		<Modal scrollBehavior="outside" size="full" isOpen={true} onClose={onCloseModal}>
			<ModalOverlay backdropFilter="auto" bg="blackAlpha.300" backdropBlur="2px" />
			<ModalContent borderRadius={c.borderRadius} p="4">
				<Flex justifyContent="flex-end">
					<ModalCloseButton />
				</Flex>

				<ModalBody>
					<Container maxW="container.xl" mt="16px" mb="16px">
						<Text fontSize="x-large" fontWeight="bold" mb={4}>
							{t('Detail')}
						</Text>

						<SimpleGrid templateColumns={{ base: '1fr', md: '4fr 1fr 4fr' }} alignItems="center">
							<Flex justify="center">
								<Select
									w="100%"
									maxW="900px"
									placeholder="--"
									value={toTimelineId ?? ''}
									onChange={(e) => {
										const id = e?.target?.value;

										if (!id) {
											setToTimelineId(null);
											setToHistory(null);
											return;
										}

										setToTimelineId(id);
										loadToTimelineJson({ variables: { timelineId: id } });
									}}
								>
									{timelinesWithHistory?.map((el) => (
										<option key={el?.id} value={el?.id}>
											{`${moment(el?.createdAt).format(c.loggingDateTimeFormat)} - ${t('Edited By')}: ${
												(el?.user?.fullNameKhmer ?? '').length ? el?.user?.fullNameKhmer : 'Admin'
											}`}
										</option>
									))}
								</Select>
							</Flex>

							<Flex direction="column" align="center" m={{ base: '4px 2px 4px 2px' }}>
								<Text whiteSpace="nowrap">{t('Compare With')}</Text>
								<FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 16 }} />
							</Flex>

							<Flex justify="center">
								<Select
									w="100%"
									maxW="900px"
									value={fromTimelineId ?? ''}
									placeholder={t('Select from timeline')}
									onChange={(e) => {
										const id = e.target.value;

										if (!id) {
											setFromTimelineId(null);
											setFromHistory(null);
											return;
										}

										setFromTimelineId(id);
										if (id) loadFromTimelineJson({ variables: { timelineId: id } });
									}}
								>
									{timelinesWithHistory?.map((el) => (
										<option key={el.id} value={el.id}>
											{`${moment(el.createdAt).format(c.loggingDateTimeFormat)} - ${t('Edited By')}: ${
												(el?.user?.fullNameKhmer ?? '').length ? el?.user?.fullNameKhmer : 'Admin'
											}`}
										</option>
									))}
								</Select>
							</Flex>

							<Flex m={{ base: '4px 2px 4px 2px' }} mt={2} justify={{ sm: 'center', md: 'start' }}>
								<Text whiteSpace="nowrap" color="red" fontStyle="italic">
									{t('Attachment comparison is not supported.')}
								</Text>
							</Flex>
						</SimpleGrid>

						{loadTimelineJsonData && (
							<Flex justifyContent="center" alignItems="center" mt="8" mb="8">
								<Spinner />
							</Flex>
						)}

						<Box mt="30px">
							{/* Subject */}
							<Text fontSize="lg" mb="2" fontWeight="bold" minW={leftLabelWidth} w={leftLabelWidth}>
								{t('Subject')}
							</Text>

							<Box w="100%" _hover={{ bg: 'gray.100' }}>
								{subjectDiff ? (
									<Box mt={4} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
										<div style={{ fontSize: '15px' }} dangerouslySetInnerHTML={{ __html: subjectDiff }} />
									</Box>
								) : (
									<Box mt={4} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
										<RichTextEditorViewer value={subject} />
									</Box>
								)}
							</Box>
							{/* Reference */}
							<Box w="100%" mb={8} mt={6}>
								<Text fontSize="lg" mb="2" fontWeight="bold" minW={leftLabelWidth} w={leftLabelWidth}>
									{t('Reference')}
								</Text>

								{referencesDiff ? (
									<Box ml={4}>
										<div style={{ fontSize: '16px' }} dangerouslySetInnerHTML={{ __html: referencesDiff }} />
									</Box>
								) : (
									references?.map((item, i) => (
										<UnorderedList key={i}>
											<ListItem _hover={{ bg: 'gray.100' }}>
												<Flex flex={1} justifyContent="flex-start" alignItems="center">
													<Flex flex={1} mr={4} justifyContent="flex-start">
														<RichTextEditorViewer value={item?.description} />
													</Flex>

													<Flex justifyContent="flex-end" minWidth={12}>
														{item?.file?.url && (
															<Popover>
																<PopoverTrigger>
																	<AttachmentIcon cursor="pointer" color="blue.500" mt={1} />
																</PopoverTrigger>
																<PopoverContent>
																	<PopoverArrow />
																	<PopoverCloseButton />
																	<PopoverHeader>{t('Attachment')}</PopoverHeader>
																	<PopoverBody>
																		<Flex justify="center">
																			<DownloadAndViewButton url={item.file.url} size={item.file.size} />
																		</Flex>
																	</PopoverBody>
																</PopoverContent>
															</Popover>
														)}
													</Flex>
												</Flex>
											</ListItem>
										</UnorderedList>
									))
								)}
							</Box>

							{/* Other Attachments */}
							<Box w="100%" mb={8}>
								<Text fontSize="lg" mb="2" fontWeight="bold" minW={leftLabelWidth} w={leftLabelWidth}>
									{t('Attachments')}
								</Text>

								{attachmentDiff ? (
									<Box ml={4}>
										<div style={{ fontSize: '16px' }} dangerouslySetInnerHTML={{ __html: attachmentDiff }} />
									</Box>
								) : (
									attachings?.map((item, i) => (
										<UnorderedList key={i}>
											<ListItem _hover={{ bg: 'gray.100' }}>
												<Flex flex={1} justifyContent="flex-start" alignItems="center">
													<Flex flex={1} mr={4} justifyContent="flex-start">
														<RichTextEditorViewer value={item?.description} />
													</Flex>
													<Flex justifyContent="flex-end" minWidth={12}>
														{item?.file?.url && (
															<Popover>
																<PopoverTrigger>
																	<AttachmentIcon cursor="pointer" color="blue.500" mt={1} />
																</PopoverTrigger>
																<PopoverContent>
																	<PopoverArrow />
																	<PopoverCloseButton />
																	<PopoverHeader>{t('Attachment')}</PopoverHeader>
																	<PopoverBody>
																		<Flex justify="center">
																			<DownloadAndViewButton
																				marginLeft={0}
																				url={item?.file?.url}
																				size={item?.file?.size}
																			/>
																		</Flex>
																	</PopoverBody>
																</PopoverContent>
															</Popover>
														)}
													</Flex>
												</Flex>
											</ListItem>
										</UnorderedList>
									))
								)}
							</Box>
							{/* Description */}
							<Text fontSize="lg" mb="2" fontWeight="bold" minW={leftLabelWidth} w={leftLabelWidth}>
								{t('Description')}
							</Text>

							<Box w="100%" _hover={{ bg: 'gray.100' }}>
								{descriptionDiff ? (
									<Box mt={4} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
										<div style={{ fontSize: '15px' }} dangerouslySetInnerHTML={{ __html: descriptionDiff }} />
									</Box>
								) : (
									<Box mt={4} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
										<RichTextEditorViewer value={description} />
									</Box>
								)}
								<Divider />
							</Box>

							<Box width="60%">
								{otherAttachmentsDiff ? (
									<>
										<Text fontSize="lg" mt={4} fontWeight="bold" minW={leftLabelWidth} w={leftLabelWidth}>
											{t('Other Attachments')}
										</Text>

										<Box p={4}>
											<div style={{ fontSize: '15px' }} dangerouslySetInnerHTML={{ __html: otherAttachmentsDiff }} />
										</Box>
									</>
								) : (
									<AdditionalAttachment
										initData={additionalAttachmentData}
										onChange={(e) => {
											setAdditionalAttachmentData(e);
										}}
									/>
								)}
							</Box>
						</Box>
					</Container>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default EditedNPModal;
